import { buildFilterKeys } from "@faire/retailer-shared/filters/filterKeys";
import { ISuggestionEntry } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Interfaces";
import {
  MAX_DESKTOP_SUGGESTION_QUERY_OVERFLOW,
  MAX_SUGGESTION_ITEMS_PER_SECTION,
  MAX_SECTION_RESULT_SIZE,
  MAX_SUGGESTION_BRAND_SUGGESTIONS,
} from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/consts";
import { buildQueriesSuggestionArray } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/ResultsDropdownUtils";
import { decodeMultimodalQueryText } from "@faire/retailer-visitor-shared/features/MultimodalProductSearch/multimodalQuery";
import { isLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { localizedUrlWithQueryParams } from "@faire/retailer-visitor-shared/lib/pathUtils";
import { discoverSearch } from "@faire/retailer-visitor-shared/lib/seoPaths";
import { brandPage } from "@faire/retailer-visitor-shared/paths/index";
import { getSettingMaxNumBrandSuggestionsConfig } from "@faire/retailer-visitor-shared/settings/getSettingMaxNumBrandSuggestionsConfig";
import { getSettingMaxNumSearchQuerySuggestionsConfig } from "@faire/retailer-visitor-shared/settings/getSettingMaxNumSearchQuerySuggestionsConfig";
import { StrictLocalizeFunction } from "@faire/web/common/localization/localize";
import { ISearchMakersWithFiltersRequest } from "@faire/web-api--source/indigofair/data/ISearchMakersWithFiltersRequest";
import { ISearchRequestReferrer } from "@faire/web-api--source/indigofair/data/ISearchRequestReferrer";
import { ISearchSuggestionResponse } from "@faire/web-api--source/indigofair/data/ISearchSuggestionResponse";
import { ISuggestedBrand } from "@faire/web-api--source/indigofair/data/ISuggestedBrand";
import { ISuggestedProduct } from "@faire/web-api--source/indigofair/data/ISuggestedProduct";
import { path as c1CategoryPath } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId";
import productSearch from "@faire/web-api--source/routes/www/search";

const categoryQueriesSuggestionArray = (
  categoryQueries: Partial<Record<string, ISearchMakersWithFiltersRequest>>,
  sectionName: string,
  requestId?: string
): ISuggestionEntry<string>[] =>
  Object.keys(categoryQueries ?? {})
    .filter((suggestionKey) => categoryQueries[suggestionKey]?.category)
    .map((suggestionKey) => {
      const makersWithFiltersRequest = categoryQueries[suggestionKey]!;
      // `suggested_category_queries` only returns with category = "All Products"
      // This is only returned, when the locale is English.
      // https://fairewholesale.atlassian.net/browse/FD-40344?focusedCommentId=57566
      // Hence, we do not need to make any change here atm.
      const category = makersWithFiltersRequest.category!;
      const obj = {
        label: suggestionKey,
        url: localizedUrlWithQueryParams(
          c1CategoryPath(category, {
            filters: [buildFilterKeys(makersWithFiltersRequest.filter_keys)],
          }),
          {
            refType:
              ISearchRequestReferrer.SearchRequestReferrerType
                .SUGGESTIONS_CATEGORY_QUERIES,
            refReqId: requestId,
          }
        ),
        sectionName,
        searchData: "",
      };
      return obj;
    });

const searchesSuggestionArray = (
  searchesArray: string[],
  sectionName: string,
  type: ISearchRequestReferrer.SearchRequestReferrerType,
  requestId: string | undefined
): ISuggestionEntry<string>[] =>
  searchesArray && searchesArray.length > 0
    ? searchesArray.map((searchesValue: string) => ({
        label: decodeMultimodalQueryText(searchesValue),
        url: localizedUrlWithQueryParams(productSearch({ q: searchesValue }), {
          refType: type,
          refReqId: requestId,
        }),
        sectionName,
        searchData: searchesValue,
        requestID: requestId,
      }))
    : [];

export const buildNaturalLanguageSuggestions = (
  suggestedNaturalLanguageSearchQueries: string[],
  sectionName: string,
  requestId: string | undefined
) => {
  const naturalLanguageSuggestionArray = searchesSuggestionArray(
    suggestedNaturalLanguageSearchQueries,
    sectionName,
    ISearchRequestReferrer.SearchRequestReferrerType
      .SUGGESTIONS_NATURAL_LANGUAGE_SEARCH_EMPTY_STATE,
    requestId
  );
  return naturalLanguageSuggestionArray;
};

export const buildRecentSearches = (
  recentSearches: string[],
  sectionName: string,
  requestId: string | undefined
) => {
  const recentSearchesSuggestionArray = searchesSuggestionArray(
    recentSearches,
    sectionName,
    ISearchRequestReferrer.SearchRequestReferrerType
      .SUGGESTIONS_RECENT_SEARCHES,
    requestId
  );

  return recentSearchesSuggestionArray;
};

export const buildSuggestedTerms = (
  suggestionsResult: ISearchSuggestionResponse,
  sectionName: string,
  shouldEnableSuggestionsImpressionAllocationV2: boolean,
  strictLocalize: StrictLocalizeFunction
) => {
  const numberOfOverflowQuerySuggestions = Math.max(
    0,
    getSettingMaxNumBrandSuggestionsConfig() -
      suggestionsResult.suggested_brands?.length
  );
  const increasedNumberOfQuerySuggestions = Math.min(
    getSettingMaxNumSearchQuerySuggestionsConfig() +
      MAX_DESKTOP_SUGGESTION_QUERY_OVERFLOW,
    getSettingMaxNumSearchQuerySuggestionsConfig() +
      numberOfOverflowQuerySuggestions
  );
  const maxNumberOfQuerySuggestions =
    shouldEnableSuggestionsImpressionAllocationV2
      ? increasedNumberOfQuerySuggestions
      : MAX_SECTION_RESULT_SIZE;

  const maxNumberOfQueryAndCategorySuggestions =
    shouldEnableSuggestionsImpressionAllocationV2
      ? increasedNumberOfQuerySuggestions
      : MAX_SUGGESTION_ITEMS_PER_SECTION;

  const suggestedSearchQueriesSuggestionArray = buildQueriesSuggestionArray(
    suggestionsResult.suggested_search_queries ?? {},
    sectionName,
    suggestionsResult.request_id,
    maxNumberOfQuerySuggestions,
    strictLocalize
  )
    .concat(
      categoryQueriesSuggestionArray(
        suggestionsResult.suggested_category_queries ?? {},
        sectionName,
        suggestionsResult.request_id
      )
    )
    .slice(0, maxNumberOfQueryAndCategorySuggestions);

  return suggestedSearchQueriesSuggestionArray;
};

export const buildBrandSuggestions = (
  brandArray: ISuggestedBrand[],
  sectionName: string,
  requestId?: string
): ISuggestionEntry[] =>
  brandArray && brandArray.length > 0
    ? brandArray
        .slice(0, MAX_SUGGESTION_BRAND_SUGGESTIONS)
        .map((brandValue: ISuggestedBrand) => ({
          label: brandValue.name!,
          url: brandPage(brandValue.token!, {
            refType:
              ISearchRequestReferrer.SearchRequestReferrerType
                .SUGGESTIONS_BRANDS,
            refReqId: requestId,
          }),
          sectionName,
          searchData: brandValue,
          requestID: requestId,
        }))
    : [];

const trendingSearchUrl = (searchValue: string, refReqId?: string) => {
  if (isLoggedInRetailer()) {
    return localizedUrlWithQueryParams(productSearch({ q: searchValue }), {
      refType:
        ISearchRequestReferrer.SearchRequestReferrerType
          .SUGGESTIONS_TRENDING_SEARCHES,
      refReqId,
    });
  }
  return discoverSearch(searchValue.toLowerCase());
};

export const buildTrendingSearches = (
  searchRecommendations: ISearchSuggestionResponse.ISearchRecommendation[],
  sectionName: string,
  requestId?: string
) =>
  searchRecommendations?.map(
    (searchValue: ISearchSuggestionResponse.ISearchRecommendation) => ({
      label: searchValue.name ?? "",
      url: trendingSearchUrl(searchValue.name ?? "", requestId),
      sectionName,
      searchData: {
        image: searchValue.image,
      },
      requestID: requestId,
    })
  );

export const buildProductSuggestions = (
  productArray: ISuggestedProduct[],
  sectionName: string
) =>
  productArray && productArray.length > 0
    ? productArray.map((productValue: ISuggestedProduct) => ({
        label: productValue.name!,
        url: `/product/${productValue.token}`,
        sectionName,
        searchData: productValue,
      }))
    : [];
